/**
 * @author NikolaV
 */
import { FeatureMouseInteraction } from './interaction/FeatureMouseInteraction';
import { featuresExtent, getFeaturesBySource, removeFeaturesBySource } from './ol-utils';

import OlMap from 'ol/Map';
import Feature, { FeatureLike } from 'ol/Feature';
import Layer from 'ol/layer/Layer';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { StyleFunction } from 'ol/style/Style';

type MakeFeatureFunction<T> = (item: T, mouseInteraction?: FeatureMouseInteraction<any>, structures?: IStructures[]) => Feature | Feature[];

export interface ILayerWrapperOptions<T> {
    featureType?: FeatureType;
    styleFunction?: Function;
    makeFeature?: MakeFeatureFunction<T>;
    mouseInteraction?: FeatureMouseInteraction<any>;
    geojsonMouseInteraction?: FeatureMouseInteraction<any>;
    onMapZoom?: Function;
    onFeaturesCollectionChange?: (layer: LayerWrapper<T>, newFeatures: Feature[]) => void;
    layerBuilderFn?: () => Layer;
    zIndex?: number;
    lazyAddToMap?: boolean;
    map?: OlMap;
}

export class LayerWrapper<T> implements ILayerWrapperOptions<T> {
    featureType: FeatureType = '';
    map: OlMap = null;
    makeFeature: MakeFeatureFunction<T> = null;
    mouseInteraction?: FeatureMouseInteraction<any> = null;
    onFeaturesCollectionChange?: (layer: LayerWrapper<T>, newFeatures: Feature[]) => void;
    styleFunction?: Function;

    layerSource: VectorSource<any> = new VectorSource<any>({
        attributions: '',
        features: [],
        useSpatialIndex: true,
    });

    layer: Layer;

    allFeatures: Feature[] = []; // holds all features, because some can be hidden by filter

    isAddedToMap = false;

    private _layerOpacity = 100;

    constructor(private opts: ILayerWrapperOptions<T>) {
        Object.assign(this, opts);
        this.layer = this.buildLayer();

        if (opts.lazyAddToMap) {
            const addOrRemoveLayer = _.debounce(() => {
                // forceRerenderMap is needed because adding gt point layer will remove logpoints layer until map is moved (forced to rerender)
                // todo - might be fixed in future openlayers version
                if (this.layerSource.isEmpty()) {
                    this.removeFromMap();
                    setTimeout(this.forceRerenderMap, 30);
                } else {
                    this.addToMap(opts.map, opts.zIndex);
                    if (this.isAddedToMap) {
                        setTimeout(this.forceRerenderMap, 30);
                    }
                }
            }, 10);
            this.layerSource.on('addfeature', addOrRemoveLayer);
            this.layerSource.on('removefeature', addOrRemoveLayer);
        }
    }

    buildLayer(): Layer {
        let layer;
        if (this.opts.layerBuilderFn) {
            layer = this.opts.layerBuilderFn();
            if (layer) {
                layer.setSource(this.layerSource);
            }
        } else {
            layer = new VectorLayer({
                source: this.layerSource,
                updateWhileAnimating: false,
                updateWhileInteracting: true,
            });
        }

        if (this.opts.mouseInteraction) {
            this.opts.mouseInteraction.layerWrapper = this;
            this.opts.mouseInteraction.layer = layer;
        }

        layer?.set('id', this.featureType);
        return layer;
    }

    addToMap(map: OlMap, zIndex = 10): this {
        if (!this.layer) {
            this.layer = this.buildLayer();
        }
        if (this.isAddedToMap) return this;

        try {
            this.layer.setZIndex(zIndex);
            this.setStyleFunction(this.opts.styleFunction);
            this.map = map;
            map.addLayer(this.layer);
            this.isAddedToMap = true;
            this.onAddedToMap();
        } catch (e) {
            console.log(this.featureType, e);
        }
        return this;
    }

    removeFromMap() {
        this.map.removeLayer(this.layer);
        this.isAddedToMap = false;
        this.onRemovedFromMap();
    }

    forceRerenderMap = () => {
        const center = this.map.getView().getCenter();
        this.map.getView().setCenter([center[0] + 0.01, center[1]]);
        setTimeout(() => this.map.getView().setCenter(center), 100);
    };

    reinitLayer() {
        const z = this.layer.getZIndex();
        try {
            this.map.removeLayer(this.layer);
            this.layer.dispose();
            this.layer = null;
        } catch {}

        this.addToMap(this.map, z);
        this.setLayerOpacity(this._layerOpacity);
    }

    postProcessFeature(feature: Feature): void {}

    setStyleFunction = (fn: Function) => {
        if (typeof fn == 'function') {
            this.styleFunction = fn;
            if (typeof (this.layer as any).setStyle == 'function') {
                (this.layer as any).setStyle(this.styleFunction as StyleFunction);
            } else if (typeof (this.layer as any).getSource().setStyle == 'function') {
                (this.layer.getSource() as any).setStyle(this.styleFunction);
            }
        }
    };

    drawAll(
        items: T[],
        structures?: IStructures[]
    ): {
        features: Feature[];
        failedToCreate: T[];
    } {
        const features = [];
        const failedToCreate = [];

        for (let i = 0, ilen = items.length; i < ilen; i++) {
            let newFeature;
            try {
                newFeature = this.makeFeature(items[i], this.mouseInteraction, structures);
                if (typeof this.postProcessFeature == 'function') {
                    this.postProcessFeature(newFeature);
                }
            } catch (e) {
                failedToCreate.push(items[i]);
                console.warn(e);
                return null;
            }
            if (newFeature) {
                if (newFeature instanceof Array) {
                    for (let j = 0, jlen = newFeature.length; j < jlen; j++) {
                        if (newFeature[j]) {
                            features.push(newFeature[j]);
                        }
                    }
                } else {
                    features.push(newFeature);
                }
            } else {
                failedToCreate.push(items[i]);
            }
        }

        this.layerSource.addFeatures(features);
        if (features) {
            this.allFeatures.push(...features);
        }

        return { features, failedToCreate };
    }

    drawOne(item: T): Feature {
        const res = this.drawAll([item]).features;
        if (res) {
            this.allFeatures.push(...res);
        }
        return Array.isArray(res) ? res[0] : null;
    }

    getAjaxData(feature: Feature | FeatureLike): T {
        if (!feature) return null;
        return feature.get('data');
    }

    fetchAndDrawFile(ajaxData?: any): JQueryPromise<any> {
        return null;
    }

    clearStyles() {
        this.layerSource.getFeatures().forEach(f => f.setStyle(null));
    }

    clear() {
        this.layerSource.clear(true);
    }

    getFeatureById(id: number | string): Feature {
        let f = this.layerSource.getFeatureById(this.featureType + '_' + id);
        if (!f) {
            f = this.layerSource.getFeatureById(id + '');
        }
        return f;
    }

    removeFeatureById(id: number): boolean {
        if (this.layerSource.getFeatures().length === 0) return; // nothing to remove

        const feature = this.getFeatureById(id);
        if (feature) {
            this.layerSource.removeFeature(feature);

            if (this.allFeatures && this.allFeatures.length > 0) {
                this.allFeatures = this.allFeatures.filter(f => f.get('data').id != id);
            }
        }
        return !!feature;
    }

    removeFeatures(toRemove: Feature[]): boolean {
        if (this.layerSource.getFeatures().length === 0 || !toRemove || toRemove.length == 0) return; // nothing to remove

        const allFeatures: Feature[] = this.layerSource.getFeatures();
        const toKeep: Feature[] = [];

        for (let i = 0, ilen = allFeatures.length; i < ilen; i++) {
            const curr: Feature = allFeatures[i];
            if (toRemove.indexOf(curr) == -1) {
                toKeep.push(curr);
            }
        }
        this.layerSource.clear(false);
        this.layerSource.addFeatures(toKeep);
        if ((this as ILayerWrapperOptions<T>).onFeaturesCollectionChange) {
            (this as ILayerWrapperOptions<T>).onFeaturesCollectionChange(this, toKeep);
        }
    }

    removeFeaturesBySource(source: FeatureSource, silent = true): void {
        const newCollection = removeFeaturesBySource(this.layerSource, source, silent);

        if (newCollection && (this as ILayerWrapperOptions<T>).onFeaturesCollectionChange) {
            (this as ILayerWrapperOptions<T>).onFeaturesCollectionChange(this, newCollection);
        }
    }

    getFeaturesBySource(source: FeatureSource, invert = false): Feature[] {
        return getFeaturesBySource(this.layerSource, source, invert);
    }

    getExtentExcludeZero() {
        const nonZeroFeatures = this.layerSource.getFeatures().filter(f => {
            const data = f.get('data');
            return (
                data && data.geographicPoint && data.geographicPoint.lat != 0 && data.geographicPoint.lon != 0 && data.geographicPoint.lat != -1 && data.geographicPoint.lon != -1
            );
        });
        return featuresExtent(nonZeroFeatures);
    }

    getFeaturesCount() {
        return this.layerSource.getFeatures().length;
    }

    setLayerOpacity = (opacity: number) => {
        if (typeof opacity === 'number') {
            this.layer.setOpacity(utils.clamp(0.2, opacity / 100, 1));
            this._layerOpacity = opacity;
        } else {
            this.layer.setOpacity(1);
            this._layerOpacity = 100;
        }
    };

    onAddedToMap() {}

    onRemovedFromMap() {}
}
